import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bot, MessageCircle, RotateCcw, Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './ChatWidget.css';

const STORAGE_KEY = 'kalle-profile-chat-v2';
const MAX_MESSAGE_LENGTH = 1800;

const copy = {
  en: {
    title: 'Ask about Kalle',
    opening:
      'Hi, I’m an AI assistant for Kalle Glauch’s website. I can answer questions about his research, publications, teaching, methods and digital projects. What would you like to know?',
    disclosure: 'This AI assistant answers from a curated profile and may not know every detail.',
    privacy: 'Site history stays in this browser session. OpenAI processes and retains response state for follow-ups.',
    placeholder: 'Ask about Kalle’s work…',
    open: 'Ask about Kalle',
    close: 'Close chat',
    reset: 'New conversation',
    send: 'Send message',
    typing: 'AI assistant is preparing an answer',
    learnMore: 'Learn more',
    followUps: 'You might also ask',
    error:
      'I couldn’t generate an answer just now. Please try again, or use the website navigation to explore Kalle’s profile.',
    rateLimited: 'Too many questions were sent in a short time. Please wait a moment and try again.',
    prompts: [
      'What is his research about?',
      'Explain his dissertation',
      'Which methods does he use?',
      'Show me his digital projects',
    ],
  },
  de: {
    title: 'Ask about Kalle',
    opening:
      'Hallo, ich bin ein KI-Assistent für Kalle Glauchs Website. Ich beantworte Fragen zu seiner Forschung, seinen Publikationen, seiner Lehre, seinen Methoden und digitalen Projekten. Was möchten Sie wissen?',
    disclosure: 'Dieser KI-Assistent antwortet aus einem kuratierten Profil und kennt möglicherweise nicht jedes Detail.',
    privacy: 'Der Website-Verlauf bleibt in dieser Browser-Sitzung. OpenAI verarbeitet und speichert Antwortstatus für Folgefragen.',
    placeholder: 'Frage zu Kalles Arbeit…',
    open: 'Ask about Kalle',
    close: 'Chat schließen',
    reset: 'Neue Unterhaltung',
    send: 'Nachricht senden',
    typing: 'Der KI-Assistent formuliert eine Antwort',
    learnMore: 'Mehr erfahren',
    followUps: 'Das könnten Sie auch fragen',
    error:
      'Ich konnte gerade keine Antwort erzeugen. Bitte versuchen Sie es erneut oder nutzen Sie die Navigation, um Kalles Profil zu erkunden.',
    rateLimited: 'Es wurden zu viele Fragen in kurzer Zeit gesendet. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
    prompts: [
      'Womit beschäftigt sich seine Forschung?',
      'Erkläre seine Dissertation',
      'Welche Methoden verwendet er?',
      'Zeige mir seine digitalen Projekte',
    ],
  },
};

function makeMessage(role, text, sources = [], followUpQuestions = []) {
  return {
    id:
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    text,
    sources,
    followUpQuestions,
  };
}

function createInitialMessages(language) {
  return [makeMessage('assistant', copy[language].opening)];
}

function readSession(language) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    const messages = Array.isArray(stored?.messages)
      ? stored.messages
          .filter(
            (message) =>
              ['assistant', 'user'].includes(message?.role) &&
              typeof message?.text === 'string' &&
              message.text.length <= 5_000,
          )
          .map((message) => ({
            ...message,
            sources: Array.isArray(message.sources) ? message.sources.slice(0, 3) : [],
            followUpQuestions: Array.isArray(message.followUpQuestions)
              ? message.followUpQuestions
                  .filter((question) => typeof question === 'string' && question.length <= 240)
                  .slice(0, 2)
              : [],
          }))
          .slice(-30)
      : [];

    return {
      messages: messages.length ? messages : createInitialMessages(language),
      responseId:
        typeof stored?.responseId === 'string' && /^resp_[A-Za-z0-9_-]{10,200}$/.test(stored.responseId)
          ? stored.responseId
          : null,
    };
  } catch {
    return { messages: createInitialMessages(language), responseId: null };
  }
}

function safeSourceUrl(url) {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    return ['https:', 'mailto:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

function ChatWidget() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('de') ? 'de' : 'en';
  const ui = copy[language];
  const initialSession = useMemo(() => readSession(language), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(initialSession.messages);
  const [responseId, setResponseId] = useState(initialSession.responseId);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const launcherRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, responseId }));
    } catch {
      // A blocked or full browser store should not make the widget unusable.
    }
  }, [messages, responseId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    inputRef.current?.focus();

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        launcherRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isLoading]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const closeChat = () => {
    setIsOpen(false);
    launcherRef.current?.focus();
  };

  const resetConversation = () => {
    abortRef.current?.abort();
    setIsLoading(false);
    setInput('');
    setResponseId(null);
    setMessages(createInitialMessages(language));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const submitMessage = async (question = input) => {
    const message = question.trim();
    if (!message || message.length > MAX_MESSAGE_LENGTH || isLoading) return;

    setMessages((current) => [...current, makeMessage('user', message)]);
    setInput('');
    setIsLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          ...(responseId ? { previousResponseId: responseId } : {}),
        }),
        signal: controller.signal,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || typeof data.answer !== 'string') {
        const errorText = response.status === 429 ? ui.rateLimited : ui.error;
        setMessages((current) => [...current, makeMessage('assistant', errorText)]);
        return;
      }

      const sources = Array.isArray(data.sources)
        ? data.sources
            .map((source) => ({
              title: typeof source?.title === 'string' ? source.title : '',
              url: safeSourceUrl(source?.url),
            }))
            .filter((source) => source.title && source.url)
            .slice(0, 3)
        : [];

      const followUpQuestions = Array.isArray(data.followUpQuestions)
        ? data.followUpQuestions
            .filter((question) => typeof question === 'string' && question.trim())
            .map((question) => question.trim())
            .slice(0, 2)
        : [];

      setMessages((current) => [
        ...current,
        makeMessage('assistant', data.answer, sources, followUpQuestions),
      ]);
      if (typeof data.responseId === 'string') setResponseId(data.responseId);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setMessages((current) => [...current, makeMessage('assistant', ui.error)]);
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setIsLoading(false);
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };

  const showPrompts = messages.length === 1 && !isLoading;

  return (
    <div className="profile-chat">
      {isOpen && (
        <section
          className="profile-chat__panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="profile-chat-title"
        >
          <header className="profile-chat__header">
            <div className="profile-chat__heading">
              <span className="profile-chat__bot" aria-hidden="true">
                <Bot size={17} strokeWidth={1.8} />
              </span>
              <div>
                <h2 id="profile-chat-title">{ui.title}</h2>
                <p>AI profile assistant</p>
              </div>
            </div>
            <div className="profile-chat__header-actions">
              <button type="button" onClick={resetConversation} aria-label={ui.reset} title={ui.reset}>
                <RotateCcw size={16} />
              </button>
              <button type="button" onClick={closeChat} aria-label={ui.close}>
                <X size={18} />
              </button>
            </div>
          </header>

          <div
            className="profile-chat__messages"
            ref={messagesRef}
            aria-live="polite"
            aria-relevant="additions"
          >
            {messages.map((message) => (
              <article
                key={message.id}
                className={`profile-chat__message profile-chat__message--${message.role}`}
              >
                <span className="profile-chat__role">
                  {message.role === 'assistant' ? 'AI' : language === 'de' ? 'Sie' : 'You'}
                </span>
                <p>{message.text}</p>
                {message.sources?.length > 0 && (
                  <div className="profile-chat__sources">
                    <span>{ui.learnMore}</span>
                    {message.sources.map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target={source.url.startsWith('https:') ? '_blank' : undefined}
                        rel={source.url.startsWith('https:') ? 'noreferrer' : undefined}
                      >
                        {source.title}
                      </a>
                    ))}
                  </div>
                )}
                {message.role === 'assistant' && message.followUpQuestions?.length > 0 && (
                  <div className="profile-chat__follow-ups">
                    <span>{ui.followUps}</span>
                    {message.followUpQuestions.map((question) => (
                      <button
                        key={question}
                        type="button"
                        onClick={() => submitMessage(question)}
                        disabled={isLoading}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                )}
              </article>
            ))}

            {showPrompts && (
              <div className="profile-chat__prompts" aria-label="Suggested questions">
                {ui.prompts.map((prompt) => (
                  <button key={prompt} type="button" onClick={() => submitMessage(prompt)}>
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="profile-chat__typing" role="status" aria-label={ui.typing}>
                <span />
                <span />
                <span />
              </div>
            )}
          </div>

          <form
            className="profile-chat__composer"
            onSubmit={(event) => {
              event.preventDefault();
              submitMessage();
            }}
          >
            <label htmlFor="profile-chat-input" className="sr-only">
              {ui.placeholder}
            </label>
            <textarea
              id="profile-chat-input"
              ref={inputRef}
              rows="1"
              maxLength={MAX_MESSAGE_LENGTH}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={ui.placeholder}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="profile-chat__send"
              disabled={!input.trim() || isLoading}
              aria-label={ui.send}
            >
              <Send size={17} strokeWidth={1.8} />
            </button>
          </form>

          <footer className="profile-chat__notice">
            <p>{ui.disclosure}</p>
            <p>{ui.privacy}</p>
          </footer>
        </section>
      )}

      <button
        ref={launcherRef}
        type="button"
        className="profile-chat__launcher"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? ui.close : ui.open}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {isOpen ? <X size={20} /> : <MessageCircle size={20} />}
        {!isOpen && <span>{ui.open}</span>}
      </button>
    </div>
  );
}

export default ChatWidget;
